import { formatMetric } from "./format";

export type DecisionSnapshot = {
  id: string;
  sourceKind?: string;
  profile: string;
  status: string;
  commitSha: string | null;
  collectedAt: string;
  commitTime?: string | null;
  aggregates: Record<string, number | null>;
  metricStates?: Record<string, string>;
  methods?: Record<string, string | null>;
};

export const DECISION_KEYS = ["sourceLoc", "ccAbove", "cloneGroups"] as const;

/** Per-key measuring-recipe ids from metric evidence. */
export function methodMapOf(evidence: { key: string; methodId: string | null }[] | undefined): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  for (const row of evidence ?? []) map[row.key] = row.methodId;
  return map;
}

/** How many decision keys share the exact measuring recipe between two snapshots. */
export function comparabilityScore(
  latest: Pick<DecisionSnapshot, "methods">,
  candidate: Pick<DecisionSnapshot, "methods">,
  keys: readonly string[] = DECISION_KEYS,
): number {
  const a = latest.methods ?? {}, b = candidate.methods ?? {};
  return keys.filter((key) => {
    const x = a[key] ?? null, y = b[key] ?? null;
    return x != null && y != null && x === y;
  }).length;
}

export function pickBaseline(
  snapshots: DecisionSnapshot[],
  latest: DecisionSnapshot,
  preferredId?: string | null,
): DecisionSnapshot | null {
  const others = snapshots.filter((s) => s.id !== latest.id);
  if (preferredId && preferredId !== "week" && preferredId !== "month") {
    const preferred = others.find((s) => s.id === preferredId);
    if (preferred) return preferred;
  }
  // A baseline on the same revision as latest shows no change; prefer an older revision.
  const older = latest.commitSha ? others.filter((s) => s.commitSha !== latest.commitSha) : others;
  const pool = older.length ? older : others;
  const timeOf = (s: DecisionSnapshot) => Date.parse(s.commitTime ?? s.collectedAt);
  // Prefer a baseline measured with the same recipe on the decision keys; otherwise deltas are honestly "not comparable".
  // Comparability outranks profile tier: a comparable structural baseline answers the decision, a same-profile stranger does not.
  const tier = (s: DecisionSnapshot) =>
    s.sourceKind === "committed" && s.profile === latest.profile && s.status === "complete" ? 0
    : s.sourceKind === "committed" && s.profile === latest.profile ? 1
    : s.sourceKind === "committed" ? 2 : 3;
  if (preferredId === "week" || preferredId === "month") {
    // Nearest snapshot at least N days older than latest: the tightest window that still spans the preset.
    const anchor = timeOf(latest);
    const cutoff = anchor - (preferredId === "week" ? 7 : 30) * 86400000;
    const aged = pool.filter((s) => { const t = timeOf(s); return Number.isFinite(t) && Number.isFinite(anchor) && t <= cutoff; });
    if (aged.length) {
      const scored = aged.map((s) => ({ s, score: comparabilityScore(latest, s) }));
      const comparable = scored.filter((x) => x.score > 0);
      const cands = (comparable.length ? comparable : scored).map((x) => x.s);
      cands.sort((a, b) => timeOf(b) - timeOf(a) || tier(a) - tier(b));
      return cands[0] ?? null;
    }
  }
  // Automatic: the widest comparable window, so the card agrees with the chart slope
  // instead of reporting yesterday's noise. Comparability first, then oldest, then tier.
  const ranked = [...pool].sort(
    (a, b) => comparabilityScore(latest, b) - comparabilityScore(latest, a)
      || timeOf(a) - timeOf(b)
      || tier(a) - tier(b),
  );
  return ranked[0] ?? null;
}

export function coverageNote(latest: DecisionSnapshot, snapshots: DecisionSnapshot[]): string {
  const committed = snapshots.filter((s) => s.sourceKind === "committed").length;
  const structural = snapshots.filter((s) => s.profile === "structural").length;
  const full = snapshots.filter((s) => s.profile === "full").length;
  const bits = [
    latest.sourceKind === "worktree"
      ? "This card is the current worktree. The timeline defaults to committed history, so totals can differ."
      : "This card is a committed revision.",
    `${committed} committed snapshots (${structural} structural, ${full} full). Diagnostics need a full scan.`,
  ];
  if (latest.status !== "complete") bits.push(`Latest status is ${latest.status}, not a clean bill of health.`);
  return bits.join(" ");
}

export function measuredKeys(snapshots: { aggregates: Record<string, number | null>; metricStates?: Record<string, string> }[], keys: string[]): string[] {
  return keys.filter((key) =>
    snapshots.some((s) => {
      const state = s.metricStates?.[key];
      if (state && state !== "measured") return false;
      return typeof s.aggregates[key] === "number";
    }),
  );
}

export type BaselineCandidate = {
  id: string;
  sourceKind?: string;
  commitSha: string | null;
  commitTime?: string | null;
  collectedAt: string;
  profile: string;
  status: string;
};

/** Newest committed snapshots offered as named baselines. The active baseline is always included. */
export function baselineCandidates(
  snapshots: BaselineCandidate[],
  baselineId: string | null,
  limit = 20,
): BaselineCandidate[] {
  const time = (s: BaselineCandidate) => s.commitTime ?? s.collectedAt;
  const committed = snapshots
    .filter((s) => s.sourceKind === "committed")
    .sort((a, b) => time(b).localeCompare(time(a)) || b.collectedAt.localeCompare(a.collectedAt));
  const rows = committed.slice(0, Math.max(1, limit)).map((s) => ({
    id: s.id,
    sourceKind: s.sourceKind,
    commitSha: s.commitSha,
    commitTime: s.commitTime ?? null,
    collectedAt: s.collectedAt,
    profile: s.profile,
    status: s.status,
  }));
  if (baselineId && !rows.some((r) => r.id === baselineId)) {
    const extra = snapshots.find((s) => s.id === baselineId);
    if (extra) {
      rows.push({
        id: extra.id,
        sourceKind: extra.sourceKind,
        commitSha: extra.commitSha,
        commitTime: extra.commitTime ?? null,
        collectedAt: extra.collectedAt,
        profile: extra.profile,
        status: extra.status,
      });
    }
  }
  return rows;
}

export type ComparisonLike = {
  fileChanges?: { path: string; delta: number | null; status?: string }[];
  complexityChanges?: {
    path: string;
    name: string;
    line: number | null;
    cyclomatic: { before: number | null; after: number | null };
    cognitive: { before: number | null; after: number | null };
  }[];
};

export type TopContributor =
  | { kind: "file"; path: string; name: null; line: null; detail: string }
  | { kind: "function"; path: string; name: string; line: number | null; detail: string };

/** One-line verdict for the decision card: what moved, since when. Null when there is nothing to say. */
export function decisionVerdict(
  baseline: { commitTime?: string | null; collectedAt: string } | null,
  latest: { commitTime?: string | null; collectedAt: string },
  deltas: { key: string; absolute: number | null }[],
): string | null {
  if (!baseline) return null;
  const loc = deltas.find((m) => m.key === "sourceLoc");
  if (!loc || loc.absolute == null || loc.absolute === 0) return null;
  const signed = (n: number) => `${n > 0 ? "+" : ""}${formatMetric(n, "sourceLoc")}`;
  const files = deltas.find((m) => m.key === "fileCount");
  const fileBit = files?.absolute ? ` · ${signed(files.absolute)} files` : "";
  return `${signed(loc.absolute)} lines since ${shortDay(baseline.commitTime ?? baseline.collectedAt)}${fileBit}`;
}

function shortDay(value: string): string {
  const t = Date.parse(value);
  if (!Number.isFinite(t)) return "?";
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Largest measured movers since the baseline: biggest |LOC| files first, then biggest complexity moves. */
export function topContributors(changes: ComparisonLike, limit = 3): TopContributor[] {
  const found: TopContributor[] = [];
  const files = (changes.fileChanges ?? []).filter((f) => typeof f.delta === "number" && f.delta !== 0);
  for (const best of [...files].sort((a, b) => Math.abs(b.delta!) - Math.abs(a.delta!) || a.path.localeCompare(b.path)).slice(0, Math.max(0, limit))) {
    const delta = best.delta!;
    found.push({ kind: "file", path: best.path, name: null, line: null, detail: `Δ LOC ${delta > 0 ? "+" : ""}${delta}` });
  }
  if (found.length < limit) {
    const ranked = (changes.complexityChanges ?? []).map((f) => ({
      f,
      move: Math.max(
        Math.abs((f.cyclomatic.after ?? 0) - (f.cyclomatic.before ?? 0)),
        Math.abs((f.cognitive.after ?? 0) - (f.cognitive.before ?? 0)),
      ),
    })).filter((r) => r.move > 0);
    for (const { f } of ranked.sort((a, b) => b.move - a.move || a.f.path.localeCompare(b.f.path)).slice(0, limit - found.length)) {
      const bit = (label: string, v: { before: number | null; after: number | null }) =>
        v.after !== v.before ? `${label} ${v.before ?? "—"} → ${v.after ?? "—"}` : null;
      const detail = [bit("CC", f.cyclomatic), bit("cognitive", f.cognitive)].filter(Boolean).join(" · ") || "changed";
      found.push({ kind: "function", path: f.path, name: f.name, line: f.line, detail });
    }
  }
  return found;
}

/** Largest measured mover since the baseline: biggest |LOC| file first, else biggest complexity move. */
export function topContributor(changes: ComparisonLike): TopContributor | null {
  return topContributors(changes, 1)[0] ?? null;
}

/** Metric keys that only a full scan can measure. Structural covers cloc + Fallow. */
export const FULL_PROFILE_KEYS = new Set([
  "knipUnusedFiles",
  "knipUnusedExports",
  "knipUnusedDeps",
  "oxlintErrors",
  "oxlintWarnings",
  "compilerErrors",
  "reactDoctorFindings",
  "qltyCodeSmells",
  "qltySmells",
  "depcruiseCycles",
]);

export type CoverageSnapshot = {
  aggregates: Record<string, number | null>;
  metricStates?: Record<string, string>;
  sourceKind?: string;
  profile: string;
};

export type CoverageGap = {
  key: string;
  plottedPoints: number;
  worktreePoints: number;
  anyPoints: number;
  partialExists: boolean;
  needsFull: boolean;
};

export type CoverageReport = {
  committed: number;
  structural: number;
  full: number;
  gaps: CoverageGap[];
};

/**
 * Coverage before plotting: for each selected key, where measured points exist
 * (current selection, worktree previews, or anywhere) and what would unlock them.
 */
export function coverageGaps(
  all: CoverageSnapshot[],
  plotted: CoverageSnapshot[],
  selected: string[],
): CoverageReport {
  const committed = all.filter((s) => s.sourceKind === "committed");
  const numeric = (s: CoverageSnapshot, key: string) => typeof s.aggregates[key] === "number";
  const gaps = selected.map((key): CoverageGap => {
    const sourceKey = key === "functionCount" ? "functions" : key;
    return {
      key,
      plottedPoints: plotted.filter((s) => numeric(s, sourceKey)).length,
      worktreePoints: all.filter((s) => s.sourceKind === "worktree" && numeric(s, sourceKey)).length,
      anyPoints: all.filter((s) => numeric(s, sourceKey)).length,
      partialExists: all.some((s) => s.metricStates?.[sourceKey] === "partial"),
      needsFull: FULL_PROFILE_KEYS.has(key),
    };
  });
  return {
    committed: committed.length,
    structural: committed.filter((s) => s.profile === "structural").length,
    full: committed.filter((s) => s.profile === "full").length,
    gaps,
  };
}

/** First story keys with at least one measured point anywhere; the meaningful default selection. */
export function meaningfulDefaultKeys(
  snapshots: CoverageSnapshot[],
  keys: readonly string[],
  limit = 2,
): string[] {
  const found = keys.filter((key) => {
    const sourceKey = key === "functionCount" ? "functions" : key;
    return snapshots.some(
      (s) => typeof s.aggregates[sourceKey] === "number" && (s.metricStates?.[sourceKey] ?? "measured") === "measured",
    );
  });
  return found.slice(0, Math.max(1, limit));
}

/** Overlay-picker copy for one metric: points in the current plot, else where they exist. */
export function overlayAvailability(
  rows: { isMethodBreak?: boolean; [key: string]: unknown }[],
  all: CoverageSnapshot[],
  key: string,
  includeWorktrees: boolean,
): string {
  const sourceKey = key === "functionCount" ? "functions" : key;
  const count = rows.filter((row) => !row.isMethodBreak && typeof row[sourceKey] === "number").length;
  if (count) return `${count} measured point${count === 1 ? "" : "s"}`;
  if (
    !includeWorktrees &&
    all.some((s) => s.sourceKind === "worktree" && s.metricStates?.[sourceKey] === "measured" && typeof s.aggregates[sourceKey] === "number")
  ) {
    return "Available in worktree previews";
  }
  if (all.some((s) => s.metricStates?.[sourceKey] === "partial")) return "Only partial reports; trend withheld";
  return "No measured data in this window";
}

export type ChartPerspective = { id: string; name: string; keys: string[] };

export const OVERVIEW_DEFAULT_KEYS = ["sourceLoc", "ccAbove"];
export const CHART_PERSPECTIVES: ChartPerspective[] = [
  { id: "story", name: "Overview", keys: ["sourceLoc", "ccAbove", "cognitiveAbove", "largeFunctions", "cloneGroups", "duplicationPct", "ccPerKloc", "cognitivePerKloc"] },
  { id: "growth", name: "Growth", keys: ["sourceLoc", "testLoc"] },
  { id: "complexity", name: "Complexity", keys: ["ccAbove", "cognitiveAbove", "largeFunctions"] },
  { id: "duplication", name: "Duplication", keys: ["cloneGroups", "duplicationPct"] },
  { id: "diagnostics", name: "Diagnostics", keys: ["compilerErrors", "oxlintWarnings", "reactDoctorFindings"] },
  { id: "structure", name: "Dependencies", keys: ["depcruiseCycles", "knipUnusedDeps"] },
];

export function usablePerspectives(
  snapshots: { aggregates: Record<string, number | null>; metricStates?: Record<string, string>; commitSha?: string | null; collectedAt?: string }[],
): ChartPerspective[] {
  const usable = CHART_PERSPECTIVES.filter((view) => {
    const keys = measuredKeys(snapshots, view.keys);
    if (!keys.length) return false;
    if (view.id === "story") return true;
    const times = new Set(
      snapshots
        .filter((snapshot) => keys.some((key) => typeof snapshot.aggregates[key] === "number" && (snapshot.metricStates?.[key] ?? "measured") === "measured"))
        .map((snapshot) => snapshot.commitSha || snapshot.collectedAt || ""),
    );
    return times.size >= 2;
  });
  return usable.length ? usable : CHART_PERSPECTIVES.slice(0, 1);
}
